import { formatDateTime, parseUtcMs } from "@/utils/datetime";
import { IconButton, Text, TrashIcon } from "@carbon6/c6-frontend-design";
import { useState, type ReactNode } from "react";
import type { Memo } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { HatTextModal } from "@/components/modal";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { formatMemoAuthorInitials } from "./memoFormatters";
import type { MemoController } from "./useMemoController";
import styles from "./MemoThread.module.css";

// 메모 목록(최신순)이다. 각 항목은 작성자 이니셜 + 이름 + 작성 시각 + 본문이다. 본인/admin(can_delete)
// 메모에만 삭제 버튼을 표시한다. 삭제 전에 경량 확인 모달을 표시한다.
export function MemoThread({
  controller,
  compact = false,
}: {
  controller: MemoController;
  compact?: boolean;
}): ReactNode {
  const [pendingDelete, setPendingDelete] = useState<Memo | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { state } = controller;

  if (state.status === "loading" || state.status === "idle") {
    return <LoadingState size="sm">메모를 불러오고 있습니다.</LoadingState>;
  }
  if (state.status === "error") {
    return (
      <EmptyState
        size="sm"
        tone="error"
        action={{ label: "다시 시도", onClick: controller.reload }}
      >
        {state.message}
      </EmptyState>
    );
  }
  if (state.data.length === 0) {
    return <EmptyState size="sm">아직 메모가 없습니다.</EmptyState>;
  }

  async function confirmDelete(): Promise<void> {
    if (!controller.canMutate || pendingDelete === null) {
      return;
    }
    setDeleting(true);
    await controller.onDelete(pendingDelete.uuid);
    setDeleting(false);
    setPendingDelete(null);
  }

  return (
    <>
      <ul className={styles.list} data-compact={compact ? "true" : undefined}>
        {state.data.map((memo) => (
          <li key={memo.uuid} className={styles.item}>
            <span className={styles.avatar} aria-hidden="true">
              {formatMemoAuthorInitials(memo.author_name)}
            </span>
            <div className={styles.body}>
              <div className={styles.meta}>
                <Text variant={hatcheryTextModes["caption-bold"]} className={styles.author}>
                  {memo.author_name}
                </Text>
                <span className={styles.date}>{formatDateTime(parseUtcMs(memo.created_at))}</span>
                {controller.canMutate && memo.can_delete ? (
                  <IconButton
                    aria-label="메모를 삭제합니다."
                    title="메모를 삭제합니다."
                    size="sm"
                    variant="surface"
                    tone="danger"
                    className={styles.delete}
                    onClick={() => setPendingDelete(memo)}
                  >
                    <TrashIcon />
                  </IconButton>
                ) : null}
              </div>
              <p className={styles.content}>{memo.content}</p>
            </div>
          </li>
        ))}
      </ul>
      <HatTextModal
        open={controller.canMutate && pendingDelete !== null}
        label="메모를 삭제합니다."
        caption="이 메모를 삭제할까요? 되돌릴 수 없습니다."
        okText="삭제"
        error
        busy={deleting}
        onOkPress={() => void confirmDelete()}
        onCancelPress={() => setPendingDelete(null)}
      />
    </>
  );
}
